<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
/* global axios */
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import ConversationsAPI from 'dashboard/api/conversations';
import { useRouter } from 'vue-router';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);
const agents = computed(() => store.getters['agents/getAgents'] || []);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const explicitReturnPath = route.query.mm_return_to;
  const accountPrefix = `/app/accounts/${accountId.value}/`;
  if (
    typeof explicitReturnPath === 'string' &&
    explicitReturnPath.startsWith(accountPrefix)
  ) {
    return explicitReturnPath;
  }

  const returnStateKey = `mm-chatwoot:conversation-return:${accountId.value}:${props.chat.id}`;
  const savedReturnState = window.sessionStorage.getItem(returnStateKey);
  if (savedReturnState) {
    try {
      const { fullPath } = JSON.parse(savedReturnState);
      if (fullPath?.startsWith(accountPrefix)) return fullPath;
    } catch (error) {
      window.sessionStorage.removeItem(returnStateKey);
    }
  }

  const {
    params: {
      accountId: routeAccountId,
      inbox_id: inboxId,
      label,
      teamId,
      id: customViewId,
    },
    name,
  } = route;

  // The generic conversation route is the "Todas" inbox. The historical
  // fallback for an unscoped conversation pointed at the Mi Marciana home
  // dashboard, so mobile Back left the inbox whenever the saved return state
  // was unavailable (direct entry, reload, or WebView/session restoration).
  // Make the route deterministic instead of depending on browser history.
  if (name === 'inbox_conversation') {
    return {
      name: 'conversation_home',
      params: { accountId: routeAccountId || accountId.value },
      query: { ...route.query, messageId: undefined },
    };
  }

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  const fallbackPath = conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
  return {
    path: fallbackPath,
    query: { ...route.query, messageId: undefined },
  };
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  const baseInbox = store.getters['inboxes/getInbox'](inboxId) || {};
  const attributes = props.chat.additional_attributes || {};
  const sourceChannel = String(
    attributes.source_channel ||
      attributes.instagram_comment_bot_source_channel ||
      ''
  ).toLowerCase();
  const integrationKind = String(attributes.integration_kind || '').toLowerCase();

  if (
    sourceChannel === 'facebook' ||
    ['facebook_comments', 'meta_facebook_comments'].includes(integrationKind)
  ) {
    return {
      ...baseInbox,
      channel_type: 'Channel::FacebookPage',
      integration_kind: 'meta_facebook_comments',
    };
  }

  if (
    sourceChannel === 'instagram' ||
    ['instagram_comments', 'meta_instagram_comments'].includes(integrationKind)
  ) {
    return {
      ...baseInbox,
      channel_type: 'Channel::Instagram',
      integration_kind: 'meta_instagram_comments',
    };
  }

  return baseInbox;
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !currentContact.value?.blocked
);

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};

const openInternalTask = () => {
  router.push({
    name: 'internal_inbox',
    params: { accountId: accountId.value },
    query: {
      compose: 'task',
      conversation_id: currentChat.value?.id || props.chat.id,
    },
  });
};

const showCaseManager = ref(false);
const savingCase = ref(false);
const selectedCaseType = ref('urgent');
const caseForm = ref({
  active: false,
  stage: 'needs_action',
  summary: '',
  reason: '',
  responsible_id: '',
  order_number: '',
});
const caseDefinitions = [
  { key: 'urgent', label: '🚨 Urgente' },
  { key: 'shipping', label: '📦 Envío' },
  { key: 'returns', label: '🔄 Cambio/devolución' },
];
const caseStages = computed(() => {
  const stages = {
    urgent: [{ key: 'needs_action', label: 'Necesita actuación' }],
    shipping: [
      { key: 'needs_action', label: 'Necesita actuación' },
      { key: 'waiting', label: 'Esperando' },
    ],
    returns: [
      { key: 'needs_action', label: 'Necesita actuación' },
      { key: 'waiting_customer', label: 'Esperando clienta' },
      { key: 'in_progress', label: 'En proceso' },
    ],
  };
  return stages[selectedCaseType.value];
});

const chatLabels = computed(() =>
  (currentChat.value?.labels || props.chat.labels || []).map(label =>
    String(typeof label === 'string' ? label : label.title || label.name || '').toLowerCase()
  )
);

const inferredCaseIsOpen = type => {
  if (type === 'urgent') {
    return currentChat.value?.priority === 'urgent' || chatLabels.value.includes('incidencia-urgente');
  }
  if (type === 'shipping') return chatLabels.value.includes('incidencia-envio');
  return chatLabels.value.includes('cambio-devolucion-pendiente');
};

const activeCaseTypes = computed(() =>
  caseDefinitions
    .filter(definition => {
      const data = currentChat.value?.custom_attributes?.mi_marciana_cases?.[definition.key];
      return data?.status === 'open' || inferredCaseIsOpen(definition.key);
    })
    .map(definition => definition.key)
);

const loadCaseForm = type => {
  selectedCaseType.value = type;
  const data = currentChat.value?.custom_attributes?.mi_marciana_cases?.[type] || {};
  caseForm.value = {
    active: data.status === 'open' || inferredCaseIsOpen(type),
    stage: data.stage || 'needs_action',
    summary: data.summary || '',
    reason: data.reason || '',
    responsible_id: data.responsible_id || '',
    order_number: data.order_number || '',
  };
};

const openCaseManager = () => {
  loadCaseForm(activeCaseTypes.value[0] || 'urgent');
  showCaseManager.value = true;
};

const saveCase = async () => {
  savingCase.value = true;
  try {
    await axios.patch(
      `/api/v1/accounts/${accountId.value}/dashboard_cases/${currentChat.value.id}`,
      {
        case_type: selectedCaseType.value,
        status: caseForm.value.active ? 'open' : 'resolved',
        stage: caseForm.value.stage,
        summary: caseForm.value.summary,
        reason: caseForm.value.reason,
        responsible_id: caseForm.value.responsible_id || null,
        order_number: caseForm.value.order_number,
      }
    );
    await store.dispatch('getConversation', currentChat.value.id);
    showCaseManager.value = false;
    useAlert('Caso actualizado correctamente');
  } catch (error) {
    useAlert('No se ha podido actualizar el caso');
  } finally {
    savingCase.value = false;
  }
};

const historyAvailable = ref(false);
const historyMessageCount = ref(0);
const historyChecking = ref(false);
const historyImporting = ref(false);

const checkUnipileHistory = async () => {
  const activeChat = currentChat.value || props.chat;
  const conversationId = activeChat?.id || props.chat?.id;
  const chatId =
    activeChat?.additional_attributes?.unipile_chat_id ||
    props.chat?.additional_attributes?.unipile_chat_id;

  historyAvailable.value = false;
  historyMessageCount.value = 0;

  if (!conversationId || !chatId) return;

  historyChecking.value = true;

  try {
    const response =
      await ConversationsAPI.getUnipileHistoryStatus(conversationId);

    historyAvailable.value = Boolean(response.data?.available);
    historyMessageCount.value = Number(response.data?.available_messages || 0);
  } catch (error) {
    console.error('Error comprobando historial Unipile', error);
  } finally {
    historyChecking.value = false;
  }
};

const importUnipileHistory = async () => {
  if (historyImporting.value) return;

  const confirmed = window.confirm(
    `Hay ${historyMessageCount.value} mensajes antiguos disponibles. ¿Quieres importarlos?`
  );

  if (!confirmed) return;

  historyImporting.value = true;

  try {
    const response = await ConversationsAPI.importUnipileHistory(
      currentChat.value?.id || props.chat.id
    );

    window.alert(
      response.data?.already_running
        ? 'La importación ya está en curso y continuará por partes.'
        : 'La importación ha comenzado. Se descargará poco a poco en segundo plano.'
    );

    window.location.reload();
  } catch (error) {
    window.alert(
      error?.response?.data?.error || 'No se pudo importar el historial.'
    );
  } finally {
    historyImporting.value = false;
  }
};

watch(
  () => [
    currentChat.value?.id,
    currentChat.value?.additional_attributes?.unipile_chat_id,
    props.chat?.id,
  ],
  checkUnipileHistory,
  { immediate: true }
);
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-1 overflow-hidden text-xs conversation--header--actions text-n-slate-11 text-ellipsis whitespace-nowrap"
        >
          <button
            type="button"
            class="truncate text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0 cucursor-pointer"
            @click="copyConversationId"
          >
            {{ `#${chat.id}` }}
          </button>
          <span v-if="hasMultipleInboxes">•</span>
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span v-if="isSnoozed">•</span>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <button
        v-if="historyAvailable"
        type="button"
        class="px-3 py-1.5 text-xs font-medium text-white rounded-lg bg-n-blue-9 hover:bg-n-blue-10 disabled:opacity-50"
        :disabled="historyChecking || historyImporting"
        @click="importUnipileHistory"
      >
        {{
          historyImporting
            ? 'Importando…'
            : `Importar historial (${historyMessageCount})`
        }}
      </button>
      <ConversationCallButton :inbox="inbox" :chat="currentChat" />
      <button
        type="button"
        class="relative rounded-lg bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-200"
        title="Gestionar incidencias y casos pendientes"
        @click="openCaseManager"
      >
        Casos
        <span
          v-if="activeCaseTypes.length"
          class="ml-1 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white"
        >{{ activeCaseTypes.length }}</span>
      </button>
      <button
        type="button"
        class="px-3 py-1.5 text-xs font-medium rounded-lg bg-pink-100 text-pink-700 hover:bg-pink-200"
        title="Crear mensaje o tarea interna vinculada a esta clienta"
        @click="openInternalTask"
      >
        Nota / tarea
      </button>
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>

  <div
    v-if="showCaseManager"
    class="fixed inset-0 z-[80] grid place-items-center bg-black/50 p-3"
    @click.self="showCaseManager = false"
  >
    <div class="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-lg flex-col overflow-hidden rounded-3xl bg-white p-5 text-slate-900 shadow-2xl">
      <div class="flex items-start justify-between gap-3">
        <div>
          <h2 class="text-xl font-bold">Gestionar caso</h2>
          <p class="text-sm text-slate-500">{{ currentContact.name }}</p>
        </div>
        <button class="grid size-9 place-items-center rounded-full bg-slate-100 text-xl" @click="showCaseManager = false">×</button>
      </div>

      <nav class="mt-4 flex gap-2 overflow-x-auto">
        <button
          v-for="definition in caseDefinitions"
          :key="definition.key"
          type="button"
          class="shrink-0 rounded-full px-3 py-2 text-xs font-semibold"
          :class="selectedCaseType === definition.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'"
          @click="loadCaseForm(definition.key)"
        >
          {{ definition.label }}
        </button>
      </nav>

      <div class="mt-4 min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
        <label class="flex items-center gap-3 rounded-2xl border border-slate-200 p-3 font-semibold">
          <input v-model="caseForm.active" type="checkbox" />
          Caso abierto y pendiente
        </label>
        <label class="block text-sm font-semibold">
          Estado
          <select v-model="caseForm.stage" class="mt-1 w-full rounded-xl border border-slate-200 bg-white p-3 font-normal">
            <option v-for="stage in caseStages" :key="stage.key" :value="stage.key">{{ stage.label }}</option>
          </select>
        </label>
        <label class="block text-sm font-semibold">
          Resumen del problema
          <textarea v-model="caseForm.summary" rows="3" class="mt-1 w-full resize-none rounded-xl border border-slate-200 p-3 font-normal" placeholder="Qué ocurre y qué falta hacer" />
        </label>
        <label v-if="selectedCaseType === 'urgent'" class="block text-sm font-semibold">
          Por qué es urgente
          <textarea v-model="caseForm.reason" rows="2" class="mt-1 w-full resize-none rounded-xl border border-slate-200 p-3 font-normal" placeholder="Motivo concreto de la urgencia" />
        </label>
        <label class="block text-sm font-semibold">
          Responsable
          <select v-model="caseForm.responsible_id" class="mt-1 w-full rounded-xl border border-slate-200 bg-white p-3 font-normal">
            <option value="">Sin asignar</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">{{ agent.name }}</option>
          </select>
        </label>
        <label class="block text-sm font-semibold">
          Pedido relacionado
          <input v-model="caseForm.order_number" class="mt-1 w-full rounded-xl border border-slate-200 p-3 font-normal" placeholder="#12345" />
        </label>
      </div>

      <button
        type="button"
        class="mt-4 w-full rounded-2xl bg-slate-900 p-3.5 font-bold text-white disabled:opacity-50"
        :disabled="savingCase"
        @click="saveCase"
      >
        {{ savingCase ? 'Guardando…' : caseForm.active ? 'Guardar caso' : 'Resolver y cerrar caso' }}
      </button>
    </div>
  </div>
</template>
